import type { MsalGuardAuthRequest } from '@azure/msal-angular';

import type { Maybe, ValueOf } from '@evc/web-components';
import { objectMap } from '@evc/web-components';

import { getUrisConfig } from '../../../features/config/config.default';
import type { ApplicationURIs } from '../../../features/config/config.type';
import {
  AUTH_INITIALIZATION_STRATEGIES,
  type AvailableLanguages,
  ENVIRONMENT,
  type PlatformEnv,
} from '../../../features/config/env.type';
import { Guid } from '../../../features/types/api-objects.type';
import type {
  AuthAuthorities,
  AuthB2CPolicies,
  AuthConfig,
  AuthPolicies,
  AuthRedirects,
  AuthStorageKeys,
} from './auth.type';

/** compute clean config for auth based on some .env config
 * this one is special as it is use by authProviders
 * important to note that environment will not be parsed and defaulted before this
 */
export function getAuthConfig(environment: PlatformEnv): Maybe<AuthConfig> {
  const {
    ENV,
    AUTH_ENABLED,
    AUTH_CLIENT_ID,
    AUTH_FAIL_ROUTE,
    AUTH_FORCE_ORGANIZATION,
    AUTH_FORCE_LICENSE,
    AUTH_PROMPT_ORGANIZATIONS,
    AUTH_DEFAULT_PROMPT,
    AUTH_ENABLE_SESSION_ON_RELOAD,
    AUTH_INITIALIZATION_STRATEGY,
    AUTH_IGNORE_AUTHORITY_DOMAIN,
    AUTH_CAPTCHA_BYPASS_TOKEN,
    USE_CONNECT_PLATFORM,
    // AUTH_USE_LEGACY_DOMAIN,
  } = environment ?? {};

  if (!USE_CONNECT_PLATFORM) return undefined;

  const env = ENV;
  const clientId = AUTH_CLIENT_ID;
  const enabled = AUTH_ENABLED ?? !!clientId;
  const forceOrganization = AUTH_FORCE_ORGANIZATION || false;
  const forceLicense = AUTH_FORCE_LICENSE || false;
  const promptOrganizations = AUTH_PROMPT_ORGANIZATIONS || false;
  const prompt = AUTH_DEFAULT_PROMPT;
  const enableSessionOnReload = AUTH_ENABLE_SESSION_ON_RELOAD || false;
  const initializationStrategy =
    AUTH_INITIALIZATION_STRATEGY || AUTH_INITIALIZATION_STRATEGIES.FORCE_REFRESH;

  const useAuthorityDomain = !AUTH_IGNORE_AUTHORITY_DOMAIN;

  if (!clientId) return undefined;

  const tenant = (() => {
    switch (env) {
      case ENVIRONMENT.production:
        return 'evidentconnect';
      case ENVIRONMENT.staging:
        return 'evcpltidpstg2';
      default:
        return 'evcpltidpdev';
    }
  })();

  if (!tenant) return undefined;

  const authorityDomain = (() => {
    if (!useAuthorityDomain) return `${tenant}.b2clogin.com`;
    /*
    const { hostname } = window.location;
    const forceLegacy = hostname === 'localhost' && AUTH_USE_LEGACY_DOMAIN;

    const local = AUTH_USE_LEGACY_DOMAIN
      ? 'dev-evcnt.com'
      : 'connect.dev-evcnt.com';

    const development = forceLegacy || /^(?!connect\.)([\w-]+\.)?dev-evcnt\.com$/.test(hostname)
      ? 'dev-evcnt.com'
      : 'connect.dev-evcnt.com';

    const staging = forceLegacy || /^(?!connect\.)([\w-]+\.)?stg-evcnt\.com$/.test(hostname)
      ? 'stg-evcnt.com'
      : 'connect.stg-evcnt.com';

    const production = forceLegacy || /tnmconnect\.com$/.test(hostname)
        ? 'tnmconnect.com'
        : 'connect.evidentscientific.com';

    const domains = {
      local,
      testing: local,
      development,
      staging,
      production,
    };
    */

    const domain = (() => {
      switch (env) {
        case ENVIRONMENT.production:
          return 'connect.evidentscientific.com';
        case ENVIRONMENT.staging:
          return 'connect.stg-evcnt.com';
        default:
          return 'connect.dev-evcnt.com';
      }
    })();

    return `login.${domain}`;
  })();

  const authorityTenantId = (() => {
    switch (env) {
      case ENVIRONMENT.production:
        return '3eb3cfcf-c897-4c07-913e-4f352afc0e65';
      case ENVIRONMENT.staging:
        return 'bfcee7e6-a77b-4af3-ac8d-10b277b0b91a';
      default:
        return 'f57d71df-07fa-43bd-ad31-28f8609df92b';
    }
  })();

  const policies: AuthPolicies = {
    signUp: 'b2c_1a_signup',
    signUpSignIn: 'b2c_1a_signup_signin',
    resetPassword: 'b2c_1a_passwordreset',
    // editProfile: 'b2c_1a_profileedit',
  };

  const b2cPolicies: AuthB2CPolicies = (() => {
    const AUTHORITY_URI = useAuthorityDomain
      ? `https://${authorityDomain}/${authorityTenantId}/` /* + policyName */
      : `https://${tenant}.b2clogin.com/${tenant}.onmicrosoft.com/`;

    return {
      names: policies,
      authorities: objectMap(
        policies,
        (policy: ValueOf<AuthAuthorities>) => `${AUTHORITY_URI}${policy}`,
      ),
      authorityDomain,
    };
  })();

  const storageKeys: AuthStorageKeys = {
    toid: 'evcnt_enforce_toid',
    acquireTokenRedirectFailed: 'evcnt_acquire_token_redirect_failed',
    onAuthSessionChange: 'evcnt_on_auth_session_change',
    captchaBypassToken: 'evcnt_captcha_bypass_token',
    captchaSiteKey: 'evcnt_app_recaptcha_site_key',
  };

  const uris: ApplicationURIs = getUrisConfig(environment);

  const redirects: AuthRedirects = {
    back: uris.homepage,
    success: uris.root!,
    logout: uris.logout ?? uris.public!,
    organization: uris.organization!,
    loginFailedRoute: AUTH_FAIL_ROUTE ?? 'auth-fail',
  };

  const captchaBypassToken = (() => {
    if (AUTH_CAPTCHA_BYPASS_TOKEN) return AUTH_CAPTCHA_BYPASS_TOKEN;

    const fromUrl = new URLSearchParams(window.location.search).get('cbt');
    const fromStorage = sessionStorage.getItem(storageKeys.captchaBypassToken);

    return fromUrl ?? fromStorage ?? undefined;
  })();

  return {
    enabled,
    clientId,
    redirects,
    policies,
    b2cPolicies,
    scopes: [clientId],
    authorityDomain,
    authorityTenantId,
    prompt,
    forceOrganization,
    forceLicense,
    promptOrganizations,
    captchaBypassToken,
    storageKeys,
    initializationStrategy,
    enableSessionOnReload,
    navigateToLoginRequestUrl: true,
  };
}

export type AuthRequestFactoryPayload = {
  triggerType: 'guard' | 'interceptor' | 'service';
  environment: PlatformEnv;
  config: AuthConfig;
  lang: AvailableLanguages;
  toid?: Guid;
  scopes?: string[];
};

export type CustomAuthRequest = MsalGuardAuthRequest & {
  extraQueryParameters: Record<string, string>;
  forceRefresh?: boolean;
};

/** Auth Request Factory use different scheme depending of the request type
 * Use INDETERMINATE when unsure so we just send everithing (e.g. guard/interceptor)
 */
export const AUTH_REQUEST_TYPE = {
  SILENT: 'silent',
  INTERACTIVE: 'interactive',
  INDETERMINATE: 'indeterminate',
} as const;
export type AuthRequestType = ValueOf<typeof AUTH_REQUEST_TYPE>;

/** build our auth request (login/accessToken)
 *
 * Note that MSALGuard/Interceptor may use both silent/interactive flow
 * so indeterminate add toid in both extraQueryParameters and tokenBodyParameters
 */
export function authRequestFactory(
  requestType: AuthRequestType = AUTH_REQUEST_TYPE.INDETERMINATE,
  { triggerType, environment, config, lang, toid, scopes }: AuthRequestFactoryPayload,
  extra: Partial<CustomAuthRequest> = {},
): CustomAuthRequest {
  const { b2cPolicies, prompt, redirects, clientId } = config ?? {};
  const { success: redirectUri, back: backRedirectUri } = redirects ?? {};
  const scopesWithDefault = Array.from(new Set([...(scopes ?? ([] as string[])), clientId]));
  const extraQueryParameters: Record<string, string> = {};
  const tokenBodyParameters: Record<string, string> = {};

  const authRequest: CustomAuthRequest = {
    authority: b2cPolicies.authorities.signUpSignIn as string,
    scopes: scopesWithDefault,
    prompt,
    redirectUri,
    extraQueryParameters,
    tokenBodyParameters,
    ...extra,
  };

  // For interactive auth (/authorize query string)
  if (requestType !== 'silent') {
    const { captchaBypassToken } = config;
    if (captchaBypassToken && captchaBypassToken !== '{IAC_AADB2C_CAPTCHA_BYPASS_TOKEN}') {
      authRequest.extraQueryParameters!.cbt = captchaBypassToken;
    }

    if (lang) {
      authRequest.extraQueryParameters!.ui_locales = lang;
    }

    if (backRedirectUri) {
      authRequest.extraQueryParameters!.back_redirect_uri = encodeURIComponent(backRedirectUri);
    }

    if (toid) {
      authRequest.extraQueryParameters!.toid = toid;
    }
  }
  // For silent token refresh (/token POST body)
  if (requestType !== 'interactive') {
    if (toid) {
      authRequest.tokenBodyParameters!['toid'] = toid;
    }
  }

  if (environment.DEBUG_AUTH_FLOW) {
    const account = (() => {
      if (!authRequest.account) return undefined;

      const { homeAccountId, localAccountId, idTokenClaims } = authRequest.account;
      const {
        exp,
        iat,
        auth_time,
        aud,
        organizationId,
        licenseIds,
        platformRoles,
        selectedSsoProvider,
      } = idTokenClaims ?? {};

      return {
        homeAccountId,
        localAccountId,
        idTokenClaims: {
          exp,
          iat,
          auth_time,
          aud,
          organizationId,
          licenseIds,
          platformRoles,
          selectedSsoProvider,
        },
      };
    })();

    // eslint-disable-next-line no-console
    console.debug(
      `[msal-${triggerType}]`,
      JSON.stringify({
        ...authRequest,
        account,
      }),
    );
  }

  return authRequest;
}
